import {useEffect, useMemo, useRef} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css, cva, cx} from "@panda/css";

import {DsTooltip} from "@pg-design/floating-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Radio} from "@pg-design/radio-module";
import {MaxValueFieldControlled, RadioGroupControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {Currency} from "@pg-rp/common-types";

import {creditContributionDefault} from "../../../credit_calculator/constants/credit_contribution_default";
import {creditPeriodDefault} from "../../../credit_calculator/constants/credit_period_default";
import {calculateInstallment} from "../../../credit_calculator/utils/calculate_installment";
import {calculatePriceFromInstalment} from "../../../credit_calculator/utils/calculate_price_from_instalment";
import {getCurrency} from "../../../utils/get_currency";
import {budgetFilterDefaults} from "../../constants/budget_filter_defaults";
import {IBudgetType} from "../../types/IBudgetType";

interface IProps {
    radioName: string;
    type: "list" | "switch";
    totalInputName: string;
    monthlyInputName: string;
    totalTitleTooltip?: {
        label: string;
        tooltip: string;
    };
    monthlyTitleTooltip?: {
        label: string;
        tooltip: string;
    };
    totalMax?: number;
    useBudgetDynamicStep?: boolean;
    currency?: Currency;
    inputVariant?: "row" | "column";
    radioClassName?: string;
    radioGroupClassName?: string;
    totalCenterValue?: number;
    isInModal?: boolean;
    label?: string;
    maxDisplayLabel?: string;
}

const budgetConfig = {
    total: {
        min: budgetFilterDefaults.totalMin,
        max: budgetFilterDefaults.totalMax,
        step: 5000,
        center: 1200000
    },
    monthly: {
        min: budgetFilterDefaults.monthlyMin,
        max: budgetFilterDefaults.monthlyMax,
        step: 200
    }
};

export function BudgetField({
    radioName,
    type,
    totalInputName,
    totalMax = budgetConfig.total.max,
    monthlyInputName,
    totalTitleTooltip,
    monthlyTitleTooltip,
    useBudgetDynamicStep,
    currency,
    inputVariant,
    radioClassName,
    radioGroupClassName,
    totalCenterValue,
    label,
    maxDisplayLabel
}: IProps) {
    const {setValue} = useFormContext();

    const budgetType: IBudgetType | undefined = useWatch({name: radioName});
    const totalValue = useWatch({name: totalInputName});
    const monthlyValue = useWatch({name: monthlyInputName});

    const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isFirstRenderRef = useRef(true);
    const prevBudgetTypeRef = useRef<IBudgetType | undefined>(budgetType);
    const prevTotalValueRef = useRef(totalValue);
    const prevMonthlyValueRef = useRef(monthlyValue);

    const monthlyMax = Math.round(calculateInstallment(totalMax, creditContributionDefault, creditPeriodDefault) || 0) || budgetConfig.monthly.max;

    useEffect(() => {
        if (!budgetType) return;

        const isFirstRender = isFirstRenderRef.current;
        const budgetTypeChanged = prevBudgetTypeRef.current !== budgetType;
        const totalValueChanged = prevTotalValueRef.current !== totalValue;
        const monthlyValueChanged = prevMonthlyValueRef.current !== monthlyValue;

        prevBudgetTypeRef.current = budgetType;
        prevTotalValueRef.current = totalValue;
        prevMonthlyValueRef.current = monthlyValue;
        isFirstRenderRef.current = false;

        if (!isFirstRender && budgetTypeChanged && !totalValueChanged && !monthlyValueChanged) {
            return;
        }

        const shouldCalculateFromTotal = budgetType === "total" && (isFirstRender ? totalValue : totalValueChanged);
        const shouldCalculateFromMonthly = budgetType === "monthly" && (isFirstRender ? monthlyValue : monthlyValueChanged);

        if (shouldCalculateFromTotal) {
            setValue(monthlyInputName, getNewInstallment(totalValue, monthlyMax), {shouldValidate: true});
        } else if (shouldCalculateFromMonthly) {
            setValue(totalInputName, getNewTotal(monthlyValue, totalMax), {shouldValidate: true});
        }
    }, [budgetType, monthlyValue, totalValue]);

    const currencySuffix = getCurrency({currency});

    const radioList = useMemo(() => {
        return [
            {
                labelContent: totalTitleTooltip ? (
                    <Tooltip label={totalTitleTooltip.label} tooltip={totalTitleTooltip.tooltip} />
                ) : (
                    <Text variant="info_txt_1">Kwota maksymalna</Text>
                ),
                value: "total"
            },
            {
                labelContent: monthlyTitleTooltip ? (
                    <Tooltip label={monthlyTitleTooltip.label} tooltip={monthlyTitleTooltip.tooltip} />
                ) : (
                    <Text variant="info_txt_1">Rata kredytu</Text>
                ),
                value: "monthly"
            }
        ];
    }, [totalTitleTooltip, monthlyTitleTooltip]);

    return (
        <div>
            {label && (
                <Text className={css({color: "var(--colors-gray-700)", mb: 12})} variant="body_copy_1">
                    {label}
                </Text>
            )}

            <RadioGroupControlled
                name={radioName}
                options={radioList}
                className={cx(radioGroupClassName, radioGroup({type}))}
                optionClassName={cx(css({display: "flex", alignItems: "center", justifyContent: "flex-start"}), radioClassName)}
                radioRender={
                    type === "switch"
                        ? undefined
                        : ({name, value, id, isActive, onChange, labelContent, className}) => {
                              return (
                                  <div className={css({flex: 1})}>
                                      <Radio
                                          name={name}
                                          id={id}
                                          value={value}
                                          isActive={isActive}
                                          onChange={(name, isActive, value) => {
                                              if (updateTimeoutRef.current) {
                                                  clearTimeout(updateTimeoutRef.current);
                                              }

                                              onChange(name, isActive, value);
                                          }}
                                          labelContent={labelContent}
                                          className={className}
                                      />
                                      {value === "monthly" ? (
                                          <MaxValueFieldControlled
                                              dataTestId="monthly-input"
                                              disabled={value !== budgetType}
                                              key="monthly"
                                              name={monthlyInputName}
                                              min={budgetConfig.monthly.min}
                                              max={monthlyMax}
                                              step={budgetConfig.monthly.step}
                                              rightElement={currencySuffix}
                                              maxDisplayLabel={maxDisplayLabel}
                                          />
                                      ) : (
                                          <MaxValueFieldControlled
                                              dataTestId="total-input"
                                              disabled={value !== budgetType}
                                              key="total"
                                              name={totalInputName}
                                              min={budgetConfig.total.min}
                                              max={totalMax}
                                              step={budgetConfig.total.step}
                                              rightElement={currencySuffix}
                                              isDynamicStep={useBudgetDynamicStep}
                                              centerValue={totalCenterValue || budgetConfig.total.center}
                                              maxDisplayLabel={maxDisplayLabel}
                                          />
                                      )}
                                  </div>
                              );
                          }
                }
            />

            {type === "switch" && (
                <div className={css({justifyContent: "center"})}>
                    {budgetType === "total" ? (
                        <MaxValueFieldControlled
                            dataTestId="total-input"
                            key="total"
                            name={totalInputName}
                            min={budgetConfig.total.min}
                            max={totalMax}
                            step={budgetConfig.total.step}
                            rightElement={currencySuffix}
                            isDynamicStep={useBudgetDynamicStep}
                            inputVariant={inputVariant}
                            centerValue={totalCenterValue || budgetConfig.total.center}
                            maxDisplayLabel={maxDisplayLabel}
                        />
                    ) : (
                        <MaxValueFieldControlled
                            dataTestId="monthly-input"
                            key="monthly"
                            name={monthlyInputName}
                            min={budgetConfig.monthly.min}
                            max={monthlyMax}
                            step={budgetConfig.monthly.step}
                            rightElement={currencySuffix}
                            inputVariant={inputVariant}
                            maxDisplayLabel={maxDisplayLabel}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// Helper Components
const Tooltip = ({label, tooltip}: {label: string; tooltip: string}) => {
    return (
        <div className={monthlyTitle} onClick={(e) => e.stopPropagation()}>
            <Text variant="info_txt_1">{label}</Text>
            <div onClick={(e) => e.stopPropagation()}>
                <DsTooltip popover={tooltip} type="success" className={messageTooltip}>
                    {({refs, getReferenceProps}) => (
                        <div ref={refs.setReference} {...getReferenceProps()}>
                            <InfoIcon size="1.6" wrapperSize="1.6" wrapperColor="transparent" />
                        </div>
                    )}
                </DsTooltip>
            </div>
        </div>
    );
};

// Utils
function getNewInstallment(totalValue: number, monthlyMax: number) {
    const newInstallment = calculateInstallment(totalValue, creditContributionDefault, creditPeriodDefault);

    if (!newInstallment) {
        return monthlyMax;
    }

    // Round to nearest step (200)
    const step = budgetConfig.monthly.step;
    const newInstallmentNumber = Math.round(newInstallment / step) * step;

    if (newInstallmentNumber < budgetConfig.monthly.min) {
        return budgetConfig.monthly.min;
    }

    if (newInstallmentNumber > monthlyMax) {
        return monthlyMax;
    }

    return newInstallmentNumber;
}

function getNewTotal(monthlyValue: number, totalMax: number) {
    const newTotal = calculatePriceFromInstalment(monthlyValue);

    if (!newTotal) {
        return totalMax;
    }

    // Round to nearest step (5000)
    const step = budgetConfig.total.step;
    const newTotalNumber = Math.round(parseInt(newTotal) / step) * step;

    if (newTotalNumber > totalMax) {
        return totalMax;
    }

    if (newTotalNumber < budgetConfig.total.min) {
        return budgetConfig.total.min;
    }

    return newTotalNumber;
}

const radioGroup = cva({
    base: {
        display: "flex",
        gap: 2,

        md: {
            gap: 4
        }
    },
    variants: {
        type: {
            list: {
                flexDirection: "column"
            },
            switch: {
                marginBottom: 2
            }
        }
    }
});

const monthlyTitle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    gap: 1
});

const messageTooltip = css({
    zIndex: 10000,
    maxWidth: 360
});
import {useEffect, useMemo, useRef} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css, cva, cx} from "@panda/css";

import {DsTooltip} from "@pg-design/floating-module";
import {InfoIcon} from "@pg-design/icons-module";
import {Radio} from "@pg-design/radio-module";
import {MaxValueFieldControlled, RadioGroupControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {Currency} from "@pg-rp/common-types";

import {creditContributionDefault} from "../../../credit_calculator/constants/credit_contribution_default";
import {creditPeriodDefault} from "../../../credit_calculator/constants/credit_period_default";
import {calculateInstallment} from "../../../credit_calculator/utils/calculate_installment";
import {calculatePriceFromInstalment} from "../../../credit_calculator/utils/calculate_price_from_instalment";
import {getCurrency} from "../../../utils/get_currency";
import {budgetFilterDefaults} from "../../constants/budget_filter_defaults";
import {IBudgetType} from "../../types/IBudgetType";

interface IProps {
    radioName: string;
    type: "list" | "switch";
    totalInputName: string;
    monthlyInputName: string;
    totalTitleTooltip?: {
        label: string;
        tooltip: string;
    };
    monthlyTitleTooltip?: {
        label: string;
        tooltip: string;
    };
    totalMax?: number;
    useBudgetDynamicStep?: boolean;
    currency?: Currency;
    inputVariant?: "row" | "column";
    radioClassName?: string;
    radioGroupClassName?: string;
    totalCenterValue?: number;
    isInModal?: boolean;
    label?: string;
    maxDisplayLabel?: string;
}

const budgetConfig = {
    total: {
        min: budgetFilterDefaults.totalMin,
        max: budgetFilterDefaults.totalMax,
        step: 5000,
        center: 1200000
    },
    monthly: {
        min: budgetFilterDefaults.monthlyMin,
        max: budgetFilterDefaults.monthlyMax,
        step: 200
    }
};

export function BudgetField({
    radioName,
    type,
    totalInputName,
    totalMax = budgetConfig.total.max,
    monthlyInputName,
    totalTitleTooltip,
    monthlyTitleTooltip,
    useBudgetDynamicStep,
    currency,
    inputVariant,
    radioClassName,
    radioGroupClassName,
    totalCenterValue,
    label,
    maxDisplayLabel
}: IProps) {
    const {setValue} = useFormContext();

    const budgetType: IBudgetType | undefined = useWatch({name: radioName});
    const totalValue = useWatch({name: totalInputName});
    const monthlyValue = useWatch({name: monthlyInputName});

    const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isFirstRenderRef = useRef(true);
    const prevBudgetTypeRef = useRef<IBudgetType | undefined>(budgetType);
    const prevTotalValueRef = useRef(totalValue);
    const prevMonthlyValueRef = useRef(monthlyValue);

    const monthlyMax = Math.round(calculateInstallment(totalMax, creditContributionDefault, creditPeriodDefault) || 0) || budgetConfig.monthly.max;

    useEffect(() => {
        if (!budgetType) return;

        const isFirstRender = isFirstRenderRef.current;
        const budgetTypeChanged = prevBudgetTypeRef.current !== budgetType;
        const totalValueChanged = prevTotalValueRef.current !== totalValue;
        const monthlyValueChanged = prevMonthlyValueRef.current !== monthlyValue;

        prevBudgetTypeRef.current = budgetType;
        prevTotalValueRef.current = totalValue;
        prevMonthlyValueRef.current = monthlyValue;
        isFirstRenderRef.current = false;

        if (!isFirstRender && budgetTypeChanged && !totalValueChanged && !monthlyValueChanged) {
            return;
        }

        const shouldCalculateFromTotal = budgetType === "total" && (isFirstRender ? totalValue : totalValueChanged);
        const shouldCalculateFromMonthly = budgetType === "monthly" && (isFirstRender ? monthlyValue : monthlyValueChanged);

        if (shouldCalculateFromTotal) {
            setValue(monthlyInputName, getNewInstallment(totalValue, monthlyMax), {shouldValidate: true});
        } else if (shouldCalculateFromMonthly) {
            setValue(totalInputName, getNewTotal(monthlyValue, totalMax), {shouldValidate: true});
        }
    }, [budgetType, monthlyValue, totalValue]);

    const currencySuffix = getCurrency({currency});

    const radioList = useMemo(() => {
        return [
            {
                labelContent: totalTitleTooltip ? (
                    <Tooltip label={totalTitleTooltip.label} tooltip={totalTitleTooltip.tooltip} />
                ) : (
                    <Text variant="info_txt_1">Kwota maksymalna</Text>
                ),
                value: "total"
            },
            {
                labelContent: monthlyTitleTooltip ? (
                    <Tooltip label={monthlyTitleTooltip.label} tooltip={monthlyTitleTooltip.tooltip} />
                ) : (
                    <Text variant="info_txt_1">Rata kredytu</Text>
                ),
                value: "monthly"
            }
        ];
    }, [totalTitleTooltip, monthlyTitleTooltip]);

    return (
        <div>
            {label && (
                <Text className={css({color: "var(--colors-gray-700)", mb: 12})} variant="body_copy_1">
                    {label}
                </Text>
            )}

            <RadioGroupControlled
                name={radioName}
                options={radioList}
                className={cx(radioGroupClassName, radioGroup({type}))}
                optionClassName={cx(css({display: "flex", alignItems: "center", justifyContent: "flex-start"}), radioClassName)}
                radioRender={
                    type === "switch"
                        ? undefined
                        : ({name, value, id, isActive, onChange, labelContent, className}) => {
                              return (
                                  <div className={css({flex: 1})}>
                                      <Radio
                                          name={name}
                                          id={id}
                                          value={value}
                                          isActive={isActive}
                                          onChange={(name, isActive, value) => {
                                              if (updateTimeoutRef.current) {
                                                  clearTimeout(updateTimeoutRef.current);
                                              }

                                              onChange(name, isActive, value);
                                          }}
                                          labelContent={labelContent}
                                          className={className}
                                      />
                                      {value === "monthly" ? (
                                          <MaxValueFieldControlled
                                              dataTestId="monthly-input"
                                              disabled={value !== budgetType}
                                              key="monthly"
                                              name={monthlyInputName}
                                              min={budgetConfig.monthly.min}
                                              max={monthlyMax}
                                              step={budgetConfig.monthly.step}
                                              rightElement={currencySuffix}
                                              maxDisplayLabel={maxDisplayLabel}
                                          />
                                      ) : (
                                          <MaxValueFieldControlled
                                              dataTestId="total-input"
                                              disabled={value !== budgetType}
                                              key="total"
                                              name={totalInputName}
                                              min={budgetConfig.total.min}
                                              max={totalMax}
                                              step={budgetConfig.total.step}
                                              rightElement={currencySuffix}
                                              isDynamicStep={useBudgetDynamicStep}
                                              centerValue={totalCenterValue || budgetConfig.total.center}
                                              maxDisplayLabel={maxDisplayLabel}
                                          />
                                      )}
                                  </div>
                              );
                          }
                }
            />

            {type === "switch" && (
                <div className={css({justifyContent: "center"})}>
                    {budgetType === "total" ? (
                        <MaxValueFieldControlled
                            dataTestId="total-input"
                            key="total"
                            name={totalInputName}
                            min={budgetConfig.total.min}
                            max={totalMax}
                            step={budgetConfig.total.step}
                            rightElement={currencySuffix}
                            isDynamicStep={useBudgetDynamicStep}
                            inputVariant={inputVariant}
                            centerValue={totalCenterValue || budgetConfig.total.center}
                            maxDisplayLabel={maxDisplayLabel}
                        />
                    ) : (
                        <MaxValueFieldControlled
                            dataTestId="monthly-input"
                            key="monthly"
                            name={monthlyInputName}
                            min={budgetConfig.monthly.min}
                            max={monthlyMax}
                            step={budgetConfig.monthly.step}
                            rightElement={currencySuffix}
                            inputVariant={inputVariant}
                            maxDisplayLabel={maxDisplayLabel}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

// Helper Components
const Tooltip = ({label, tooltip}: {label: string; tooltip: string}) => {
    return (
        <div className={monthlyTitle} onClick={(e) => e.stopPropagation()}>
            <Text variant="info_txt_1">{label}</Text>
            <div onClick={(e) => e.stopPropagation()}>
                <DsTooltip popover={tooltip} type="success" className={messageTooltip}>
                    {({refs, getReferenceProps}) => (
                        <div ref={refs.setReference} {...getReferenceProps()}>
                            <InfoIcon size="1.6" wrapperSize="1.6" wrapperColor="transparent" />
                        </div>
                    )}
                </DsTooltip>
            </div>
        </div>
    );
};

// Utils
function getNewInstallment(totalValue: number, monthlyMax: number) {
    const newInstallment = calculateInstallment(totalValue, creditContributionDefault, creditPeriodDefault);

    if (!newInstallment) {
        return monthlyMax;
    }

    // Round to nearest step (200)
    const step = budgetConfig.monthly.step;
    const newInstallmentNumber = Math.round(newInstallment / step) * step;

    if (newInstallmentNumber < budgetConfig.monthly.min) {
        return budgetConfig.monthly.min;
    }

    if (newInstallmentNumber > monthlyMax) {
        return monthlyMax;
    }

    return newInstallmentNumber;
}

function getNewTotal(monthlyValue: number, totalMax: number) {
    const newTotal = calculatePriceFromInstalment(monthlyValue);

    if (!newTotal) {
        return totalMax;
    }

    // Round to nearest step (5000)
    const step = budgetConfig.total.step;
    const newTotalNumber = Math.round(parseInt(newTotal) / step) * step;

    if (newTotalNumber > totalMax) {
        return totalMax;
    }

    if (newTotalNumber < budgetConfig.total.min) {
        return budgetConfig.total.min;
    }

    return newTotalNumber;
}

const radioGroup = cva({
    base: {
        display: "flex",
        gap: 2,

        md: {
            gap: 4
        }
    },
    variants: {
        type: {
            list: {
                flexDirection: "column"
            },
            switch: {
                marginBottom: 2
            }
        }
    }
});

const monthlyTitle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    gap: 1
});

const messageTooltip = css({
    zIndex: 10000,
    maxWidth: 360
});
